package com.eladfinish.bible.contest.navigation

import androidx.compose.animation.AnimatedContentTransitionScope
import androidx.compose.animation.AnimatedVisibilityScope
import androidx.compose.animation.EnterTransition
import androidx.compose.animation.ExitTransition
import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.navigation.NavBackStackEntry
import androidx.navigation.NavController
import androidx.navigation.NavDestination
import androidx.navigation.NavGraphBuilder
import androidx.navigation.NavHostController
import androidx.navigation.NavType
import androidx.navigation.compose.composable
import androidx.navigation.compose.currentBackStackEntryAsState
import androidx.navigation.toRoute
import com.eladfinish.bible.contest.common.classNameOf
import com.eladfinish.bible.contest.ui.components.toolbar.SetToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarConfiguration
import com.eladfinish.bible.contest.ui.components.toolbar.configuration.model.ToolbarHeaderComponentConfig
import com.eladfinish.bible.contest.ui.components.toolbar.model.ToolbarKind
import kotlin.reflect.KClass
import kotlin.reflect.KType

inline fun <reified T : NavigationDestination> NavGraphBuilder.composableAnimated(
    typeMap: Map<KType, NavType<*>> = emptyMap(),
    noinline enterTransition: (AnimatedContentTransitionScope<NavBackStackEntry>.() -> EnterTransition?)? = {
        slideIntoContainer(AnimatedContentTransitionScope.SlideDirection.Left)
    },
    noinline exitTransition: (AnimatedContentTransitionScope<NavBackStackEntry>.() -> ExitTransition?)? = {
        slideOutOfContainer(AnimatedContentTransitionScope.SlideDirection.Left)
    },
    noinline popEnterTransition: (AnimatedContentTransitionScope<NavBackStackEntry>.() -> EnterTransition?)? = {
        slideIntoContainer(AnimatedContentTransitionScope.SlideDirection.Right)
    },
    noinline popExitTransition: (AnimatedContentTransitionScope<NavBackStackEntry>.() -> ExitTransition?)? = {
        slideOutOfContainer(AnimatedContentTransitionScope.SlideDirection.Right)
    },
    noinline content: @Composable AnimatedVisibilityScope.(T) -> Unit,
) {
    composable<T>(
        typeMap = typeMap,
        enterTransition = enterTransition,
        exitTransition = exitTransition,
        popEnterTransition = popEnterTransition,
        popExitTransition = popExitTransition,
    ) { backStackEntry ->
        val route = backStackEntry.toRoute<T>()

        val title = route.title
        if (title != null) {
            SetToolbarConfiguration(
                // AnyToolbarConfiguration? accepts ToolbarConfiguration<out Enum<*>>
                configuration = ToolbarConfiguration<AutoToolbarId>(
                    title = ToolbarHeaderComponentConfig.Text(title),
                    subtitle = route.subtitle?.let { ToolbarHeaderComponentConfig.Text(it) },
                    kind = ToolbarKind.None,
                ),
                // Helps avoid redundant sets when only recomposing
                route::class,
                title,
                route.subtitle,
            )
        }

        content(route)
    }
}

enum class AutoToolbarId

@Composable
fun NavController.currentDestinationState(): State<NavDestination?> {
    val currentBackStackEntryState by currentBackStackEntryAsState()

    return remember {
        derivedStateOf { currentBackStackEntryState?.destination }
    }
}

val NavigationDestination.route: String?
    get() = classNameOf(this::class)

val <T : NavigationDestination> KClass<T>.screenKey: String
    get() = classNameOf(this)

fun NavHostController.hasDestination(
    destination: NavigationDestination,
): Boolean {
    return this.currentBackStack.value.any { entry ->
        entry.destination.route == destination.route
    }
}

/**
 * Pops the back stack only when there is a previous entry to land on.
 *
 * A plain [NavController.popBackStack] pops the start destination too, leaving the
 * NavHost empty (blank screen). That happens with rapid back presses: while the pop
 * animation runs, the outgoing screen's back interceptor is still registered, so each
 * extra press triggers another pop before the stack settles.
 */
fun NavController.popBackStackSafely(): Boolean {
    if (previousBackStackEntry == null) return false
    return popBackStack()
}

fun NavHostController.navigateOrPopTo(destination: NavigationDestination) {
    if (hasDestination(destination)) {
        popBackStack(
            route = destination,
            inclusive = false,
        )
    } else {
        navigate(destination)
    }
}

/**
 * Returns a stable "screen key" for the current navigation destination.
 *
 * ## Why is this needed?
 * In Compose Navigation (especially with *typed destinations*), `NavDestination.route`
 * is not always a simple, stable string:
 *
 * - **Required arguments** (without default values) are encoded as **path segments**:
 *   `Screen/{arg1}/{arg2}`
 * - **Optional / default / nullable arguments** are encoded as **query parameters**:
 *   `?arg3={arg3}&arg4={arg4}`
 *
 * Example route pattern:
 * ```
 * com.example.ViewContentTabsScreenNavigationDestination/{browseMode}/{indexPickedBook}
 * ?completeChapter={completeChapter}&showOnqelos={showOnqelos}
 * ```
 *
 * If we use this route as-is, it becomes **unstable** and unsuitable as a key
 * for things like toolbar action routing, because:
 * - It contains argument placeholders (`{...}`)
 * - It varies between destinations that conceptually represent the *same screen*
 *
 * ## What does this function do?
 * 1. Removes the query part (`?arg=...`) if present
 * 2. Splits the remaining path by `/`
 * 3. Keeps only the leading segments **until the first path argument** (`{...}`)
 * 4. Reassembles them into a stable base route
 *
 * The result is typically the fully-qualified destination class name, which is
 * stable and ideal to be used as a "screen key".
 *
 * ## Returned value
 * - A stable screen key string, or
 * - `null` if no valid key can be derived
 */
fun NavBackStackEntry.toScreenKeyOrNull(): String? {
    val route = destination.route ?: return null

    // Remove query parameters (everything after '?')
    val noQuery = route.substringBefore('?')

    // Remove typed path arguments (segments containing '{...}')
    return noQuery
        .split('/')
        .takeWhile { !it.contains('{') }
        .joinToString("/")
        .ifBlank { null }
}
